import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

type MediaItem = {
  id: string;
  r2Key: string;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  alt: string;
  createdAt: string;
  url: string;
};

type MediaSortKey = "key" | "type" | "size" | "dimensions" | "alt" | "date";

function mediaSearchText(item: MediaItem) {
  return [
    item.r2Key,
    item.mime,
    item.alt,
    item.width && item.height ? `${item.width} x ${item.height}` : "",
    formatDate(item.createdAt),
  ]
    .filter(Boolean)
    .join(" ");
}

const mediaSorters: Record<MediaSortKey, (row: MediaItem) => string | number> = {
  key: (row) => row.r2Key,
  type: (row) => row.mime,
  size: (row) => row.bytes,
  dimensions: (row) => (row.width ?? 0) * (row.height ?? 0),
  alt: (row) => row.alt,
  date: (row) => dateSortValue(row.createdAt),
};

export default function AdminMedia() {
  const [list, setList] = useState<MediaItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [altText, setAltText] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const fileRef = useRef<HTMLInputElement>(null);

  async function refresh() {
    const r = await api.get<{ media: MediaItem[] }>("/api/admin/media");
    setList(r.media);
  }

  useEffect(() => {
    refresh().finally(() => setLoaded(true));
  }, []);

  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setUploadErr("Select a file first.");
      return;
    }
    if (!altText.trim()) {
      setUploadErr("Alt text is required.");
      return;
    }
    setUploading(true);
    setUploadErr(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("alt", altText.trim());
      const res = await fetch(`${BASE}/api/admin/media`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const json: unknown = await res.json();
      if (!res.ok) {
        const msg =
          json && typeof json === "object" && "error" in json
            ? String((json as { error: unknown }).error)
            : `HTTP ${res.status}`;
        throw new Error(msg);
      }
      setAltText("");
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(item: MediaItem) {
    if (!confirm(`Delete "${item.alt || item.r2Key}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/media/${item.id}`);
    await refresh();
  }

  function fmtBytes(n: number) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  const typeOptions = useMemo(
    () =>
      ["all", ...Array.from(new Set(list.map((item) => item.mime))).sort()].map((value) => ({
        value,
        label: value === "all" ? "All types" : value,
      })),
    [list],
  );
  const filters = useMemo(
    () => [(row: MediaItem) => typeFilter === "all" || row.mime === typeFilter],
    [typeFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<MediaItem, MediaSortKey>({
    rows: list,
    search,
    initialSort: { key: "date", direction: "desc" },
    sorters: mediaSorters,
    searchText: mediaSearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Media</h1>
      </div>

      {/* Upload panel */}
      <div className="paper-card upload-card">
        <legend className="form-legend">Upload image</legend>
        <div className="upload-row">
          <label className="fld upload-file-fld">
            <span>File</span>
            <input type="file" accept="image/*" ref={fileRef} />
          </label>
          <label className="fld upload-alt-fld">
            <span>Alt text</span>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Describe the image…"
            />
          </label>
          <div className="upload-btn-wrap">
            <button type="button" className="btn-woven" onClick={handleUpload} disabled={uploading}>
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
        {uploadErr && <p className="err">{uploadErr}</p>}
      </div>

      {/* Media grid / table */}
      {!loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search media, filenames, alt text..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeOptions}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No media yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No media matches those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <th>Preview</th>
                  <SortableTh label="Key / filename" sortKey="key" sort={sort} onSort={requestSort} />
                  <SortableTh label="Type" sortKey="type" sort={sort} onSort={requestSort} />
                  <SortableTh label="Size" sortKey="size" sort={sort} onSort={requestSort} />
                  <SortableTh label="Dimensions" sortKey="dimensions" sort={sort} onSort={requestSort} />
                  <SortableTh label="Alt" sortKey="alt" sort={sort} onSort={requestSort} />
                  <SortableTh label="Created" sortKey="date" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((m) => (
                  <tr key={m.id}>
                    <td className="thumb-cell">
                      <img src={m.url} alt={m.alt} className="thumb" />
                    </td>
                    <td>
                      <span className="key-text">{m.r2Key}</span>
                    </td>
                    <td>{m.mime}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{fmtBytes(m.bytes)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {m.width && m.height ? `${m.width} x ${m.height}` : "-"}
                    </td>
                    <td>{m.alt || <span className="muted">-</span>}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(m.createdAt)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-danger sm" onClick={() => handleDelete(m)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; font-size: 0.62rem; }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; margin-bottom: 28px; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .upload-card {}
  .upload-row { display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; }
  .upload-file-fld { flex: 1 1 220px; }
  .upload-alt-fld { flex: 2 1 280px; }
  .upload-btn-wrap { padding-bottom: 1rem; }
  .fld { display: block; margin-bottom: 0; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text] {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld input[type=text]:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .fld input[type=file] { font-family: ${fonts.body}; font-size: 13px; color: ${colors.navy}; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0 0; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .thumb-cell { width: 72px; }
  .thumb { width: 64px; height: 48px; object-fit: cover; border-radius: 6px; border: 1px solid rgba(201,147,76,0.25); display: block; }
  .key-text { font-size: 11.5px; color: rgba(8,45,61,0.6); font-family: monospace; word-break: break-all; }
  .muted { color: rgba(8,45,61,0.35); }
`;
